<script setup>
import ContainerRoot from '@js/components/containers/ContainerRoot.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import ListGroup from '@js/components/lists/ListGroup.vue';
import ListItem from '@js/components/lists/ListItem.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSuccess } from '@js/utils/toast';
import { computed, ref, watch } from 'vue';

const { tenant, periodType, validations, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    periodType: {
        type: String,
        default: 'month',
    },
    validations: {
        type: Array,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const periods = ref(validations.map((validation) => ({ ...validation })));

const loadingKey = ref(null);

const hasChanges = ref(false);

const title = computed(() =>
    periodType === 'quarter'
        ? `Visibilité des rapports trimestriels — ${tenant.name}`
        : `Visibilité des rapports mensuels — ${tenant.name}`,
);

function periodKey(period) {
    return `${periodType}-${period.year}-${period.period}`;
}

function toggle(period, value) {
    const previous = period.validated;

    period.validated = value;
    loadingKey.value = periodKey(period);

    axios
        .post(
            route(
                'front.projectManagement.customer.postToggleReportValidation',
                { tenant: tenant.id },
            ),
            {
                periodType,
                year: period.year,
                period: period.period,
                validated: value,
            },
        )
        .then(() => {
            hasChanges.value = true;

            toastSuccess(
                value
                    ? 'Rapport rendu visible au client.'
                    : 'Rapport masqué au client.',
            );
        })
        .catch((error) => {
            period.validated = previous;
            console.error(error);
            toastError('Impossible de mettre à jour la visibilité du rapport.');
        })
        .finally(() => {
            loadingKey.value = null;
        });
}

watch(isOpen, (open) => {
    if (!open && hasChanges.value) {
        hasChanges.value = false;

        if (callback) {
            callback();
        }
    }
});
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            {{ title }}
        </template>

        <template #content>
            <ContainerRoot>
                <ListGroup>
                    <ListItem
                        v-for="period in periods"
                        :key="periodKey(period)"
                    >
                        <SwitchField
                            :field-id="`report-validated-${periodKey(period)}`"
                            :field-name="`report-validated-${periodKey(period)}`"
                            :label="period.formattedPeriod"
                            :model-value="period.validated"
                            :disabled="loadingKey === periodKey(period)"
                            inline
                            @update:model-value="
                                (value) => toggle(period, value)
                            "
                            without-margin
                        />
                    </ListItem>
                </ListGroup>
            </ContainerRoot>
        </template>
    </DialogDrawer>
</template>
